import React from 'react'

import * as c from '../../../common'
import {
  TOTAL_PENDING_LABEL,
  totalCostPendingApply,
} from '../../../common/helpers/totalCostDisplay'
import * as r from '../../../react-utils'

import Button from '@mui/material/Button'

import * as gs from '../../../app/gStyles'

import ContractLogo from './ContractLogo'
import ReportPages, { horizontalPad } from './ReportPages'
//@ts-ignore
import styles from './SecurityContract.module.css'

/**
 * Dedicated M+S Electric Certificate of Completion — split off the shared
 * `CertOfCompletion.tsx` (Luis, 2026-08-06): that component is used by
 * FE/CE/HC too, and changes meant only for MSE ("sin afectar de ninguna
 * forma los demás contratos") can't safely land there. Same "own vaina"
 * rule as `MSElectricContract.tsx`.
 *
 * Differences from the shared component, all MSE-only:
 * - No companyBorder (orange stripes) — Maria G's same request as the main
 *   contract.
 * - Buyer/Property Address/Home Phone/E-mail/Rep Name/Rep Phone are now
 *   editable inputs instead of read-only spans (Rep Phone previously had
 *   no backing field at all — see `home_improvement_rep_phone`, schema.ts).
 * - "1. Scope of Completed Work" uses the exact same single-line "Smart
 *   Home" + TOTAL/Monthly Payment block as `MSElectricContract.tsx`'s
 *   Scope of Work (Luis: "dejar el Scope of Work igual que el que
 *   modificamos") — a flat price, never an itemized bullet list naming
 *   individual equipment (alarm-safe intent, same as that file), NOT the
 *   shared component's itemized "System Installed" list.
 *
 * Caballero's Electric (CE) is a possible future candidate for the same
 * treatment, pending Luis's confirmation once MSE's changes are approved —
 * not done here.
 */
export type MSElectricCertOfCompletionProps = c.r<{
  customerID: string
  isPDF: boolean
  noPDF?: boolean
  readOnly?: boolean
  toggleSecCompletionSignature(): void
}>

const MSE_COMPANY = {
  email: 'info@mselectriclv.com',
  license: '0078629',
  name: 'M+S Electric',
  phone: '702-545-0980',
  website: 'www.mselectriclv.com',
}

export default React.memo<MSElectricCertOfCompletionProps>(
  function MSElectricCertOfCompletion({
    customerID,
    isPDF,
    noPDF,
    readOnly,
    toggleSecCompletionSignature,
  }) {
    //#region global
    const [customerData] = r.useCustomer(customerID)

    const {
      security_completion_sign_customer,
      security_completion_sign_customer_date,

      security_contract_sign_company,
      security_contract_sign_customer,
      solarCompany,

      security_cams,
      security_cams_show,
      security_cams_customer_cost,
      security_system,
      security_system_show,
      security_system_customer_cost,
      security_tv,
      security_tv_show,
      security_tv_customer_cost,
      security_wifi_cameras,
      security_wifi_cameras_show,
      security_wifi_cameras_customer_cost,
      security_home_automation,
      security_home_automation_show,
      security_home_automation_customer_cost,
    } = customerData

    const { appliedLoanGrandTotalWDF, paymentMethodVal, term } =
      r.usePayment(customerID)

    const [customerName, , customerNameHandle] = r.useCustomerFieldInput(
      customerID,
      'customerName',
    )
    const [customerAddress, , customerAddressHandle] = r.useCustomerFieldInput(
      customerID,
      'customerAddress',
    )
    const [customerPhone, , customerPhoneHandle] = r.useCustomerFieldInput(
      customerID,
      'customerPhone',
    )
    const [customerEmail, , customerEmailHandle] = r.useCustomerFieldInput(
      customerID,
      'customerEmail',
    )
    const [homeRep, , homeRepHandle] = r.useCustomerFieldInput(
      customerID,
      'homeRep',
    )
    const [repPhone, , repPhoneHandle] = r.useCustomerFieldInput(
      customerID,
      'home_improvement_rep_phone',
    )

    const [coBuyer, , coBuyerHandle] = r.useCustomerFieldInput(
      customerID,
      'security_contract_co_buyer',
    )
    const [workOrderVal, , workOrderHandle] = r.useCustomerFieldInput(
      customerID,
      'security_completion_work_order',
    )
    const [completionDateVal, , completionDateHandle] = r.useCustomerFieldInput(
      customerID,
      'security_completion_date',
    )

    // Exact same "Smart Home" flat-price computation as
    // MSElectricContract.tsx's Scope of Work — kept as its own local copy
    // (not imported/shared) per the "own vaina" rule; see that file's
    // comment for the full rationale. Never itemized/named individually
    // (alarm-safe intent).
    const smartHomePrice = React.useMemo((): number => {
      const categories = [
        [security_cams, security_cams_show, security_cams_customer_cost],
        [security_system, security_system_show, security_system_customer_cost],
        [security_tv, security_tv_show, security_tv_customer_cost],
        [
          security_wifi_cameras,
          security_wifi_cameras_show,
          security_wifi_cameras_customer_cost,
        ],
        [
          security_home_automation,
          security_home_automation_show,
          security_home_automation_customer_cost,
        ],
      ]
      return categories.reduce(
        (sum, [enabled, show, cost]) =>
          enabled === 'no' || show === 0 ? sum : sum + c.parseFloating(cost),
        0,
      )
    }, [
      security_cams,
      security_cams_show,
      security_cams_customer_cost,
      security_system,
      security_system_show,
      security_system_customer_cost,
      security_tv,
      security_tv_show,
      security_tv_customer_cost,
      security_wifi_cameras,
      security_wifi_cameras_show,
      security_wifi_cameras_customer_cost,
      security_home_automation,
      security_home_automation_show,
      security_home_automation_customer_cost,
    ])

    // Same reconciliation rule as MSElectricContract.tsx (B72): TOTAL always
    // uses the full grand total, never a reduced/itemized-only sum.
    const totalCost = r.useTotalCost('customer', customerID)

    // Read-only mirror of MSElectricContract.tsx's `scopeOfWorkTotal` (Luis,
    // 2026-09-03, Ciara Wilson) — this doc's own docstring says its Scope of
    // Completed Work must stay identical to that file's Scope of Work, so
    // TOTAL here has to read the same frozen `home_improvement_contract_price`
    // instead of recomputing `totalCost` live, or the two pages disagree with
    // each other on a signed customer the moment `_customer_cost` drifts
    // (V64). Not written here — MSElectricContract.tsx's effect is the one
    // writer (V67: single seed source, once signed nothing writes again).
    const [contractPriceForTotal] = r.useCustomerFieldInput(
      customerID,
      'home_improvement_contract_price',
    )
    const scopeOfWorkTotal = c.parseFloating(contractPriceForTotal) || totalCost
    const [customerForTotal] = r.useCustomer(customerID)
    const totalPending = totalCostPendingApply(customerForTotal, c.IS_MAIN)

    const monthlyPayment = React.useMemo((): number => {
      if (paymentMethodVal !== 'financing' || !term || !term.years) return 0
      if (term.interest === 0) {
        return appliedLoanGrandTotalWDF / term.years / 12
      }
      return c.calcLoanMonthlyPay(
        appliedLoanGrandTotalWDF,
        term.interest * 100,
        term.years,
      )
    }, [appliedLoanGrandTotalWDF, paymentMethodVal, term])
    //#endregion

    const buttonSignatureStyle = React.useMemo(
      () => ({
        backgroundColor: c.companyColors[solarCompany],
        '&:hover': { backgroundColor: c.companyColors[solarCompany] },
        width: 256,
      }),
      [solarCompany],
    )

    return (
      <ReportPages customerID={customerID} hPad noPDF={noPDF}>
        <div style={sx.logo}>
          <ContractLogo isPDF={isPDF} readOnly={readOnly} size="l" />
        </div>
        {gapBig}
        <div style={sx.headerText}>
          <p className={styles['heading']} style={sx.headerHeading}>
            {MSE_COMPANY.name}
          </p>

          <p style={sx.companyInfo}>Phone: {MSE_COMPANY.phone}</p>
          <p style={sx.companyInfo}>Email: {MSE_COMPANY.email}</p>
          <p style={sx.companyInfo}>Website: {MSE_COMPANY.website}</p>
          <p style={sx.companyInfo}>Contractor Lic #: {MSE_COMPANY.license}</p>
        </div>
        {gapMedium}
        <p className={styles['subheading']}>CERTIFICATE OF COMPLETION</p>
        {gapMedium}
        <p style={sx.node}>
          Buyer:{' '}
          <input
            className={styles['input-inline-wide']}
            disabled={readOnly || !!security_completion_sign_customer}
            onChange={customerNameHandle}
            title="Buyer"
            value={customerName}
          />{' '}
          Co-Buyer:{' '}
          <input
            className={styles['input-inline-wide']}
            disabled={
              readOnly ||
              !!security_completion_sign_customer ||
              !!security_contract_sign_company ||
              !!security_contract_sign_customer
            }
            onChange={coBuyerHandle}
            title="Co-Buyer"
            value={coBuyer}
          />
        </p>
        <p style={sx.node}>
          Property Address:{' '}
          <input
            className={styles['input-inline-wide']}
            disabled={readOnly || !!security_completion_sign_customer}
            onChange={customerAddressHandle}
            title="Property Address"
            value={customerAddress}
          />
        </p>
        <p style={sx.node}>
          Home / Cell Phone:{' '}
          <input
            className={styles['input-inline-wide']}
            disabled={readOnly || !!security_completion_sign_customer}
            onChange={customerPhoneHandle}
            title="Phone"
            value={customerPhone}
          />{' '}
          E-mail:{' '}
          <input
            className={styles['input-inline-wide']}
            disabled={readOnly || !!security_completion_sign_customer}
            onChange={customerEmailHandle}
            title="Email"
            value={customerEmail}
          />
        </p>
        <p style={sx.node}>
          Work Order / Project #:
          <input
            className={styles['input-inline-wide']}
            disabled={readOnly || !!security_completion_sign_customer}
            onChange={workOrderHandle}
            title="Work order number"
            value={workOrderVal}
          />
        </p>
        <p style={sx.node}>
          Completion Date:
          <input
            className={styles['input-inline-wide']}
            disabled={readOnly || !!security_completion_sign_customer}
            onChange={completionDateHandle}
            title="Completion date"
            value={completionDateVal}
          />
        </p>
        {gapSmall}
        <p style={sx.node}>
          Rep Name:{' '}
          <input
            className={styles['input-inline-wide']}
            disabled={readOnly || !!security_completion_sign_customer}
            onChange={homeRepHandle}
            title="Rep Name"
            value={homeRep}
          />{' '}
          Rep Phone:{' '}
          <input
            className={styles['input-inline-wide']}
            disabled={readOnly || !!security_completion_sign_customer}
            onChange={repPhoneHandle}
            title="Rep Phone"
            value={repPhone}
          />
        </p>
        {gapMedium}
        <p style={sx.node}>
          <span style={sx.bold}>1. Scope of Completed Work.</span> This document
          certifies that {MSE_COMPANY.name} has completed the installation and
          commissioning of the following system at the service address listed
          above:
        </p>
        {smartHomePrice > 0 && (
          <div style={gs.flowRootWidth100}>
            <p style={sx.node}>
              Smart Home
              {c.blankChar(20)}
              {Intl.NumberFormat('en-US', {
                currency: 'USD',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
                style: 'currency',
              }).format(smartHomePrice)}
            </p>
            {gapSmall}
            <p style={sx.grandTotal}>
              <span style={sx.bold}>
                TOTAL: {c.blankChar(12)}
                {totalPending
                  ? TOTAL_PENDING_LABEL
                  : Intl.NumberFormat('en-US', {
                      currency: 'USD',
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                      style: 'currency',
                    }).format(scopeOfWorkTotal)}
              </span>
            </p>
            {monthlyPayment > 0 && (
              <p style={sx.grandTotal}>
                Monthly Payment: {c.blankChar(4)}
                {Intl.NumberFormat('en-US', {
                  currency: 'USD',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                  style: 'currency',
                }).format(monthlyPayment)}
                /mo
              </p>
            )}
          </div>
        )}
        <div style={gs.flowRootWidth100}>
          <p style={sx.node}>Services Included:</p>

          <p style={sx.nodeBullet}>• Professional installation & labor</p>
          <p style={sx.nodeBullet}>• System configuration</p>
          <p style={sx.nodeBullet}>
            • Mobile application & remote access setup
          </p>
          <p style={sx.nodeBullet}>• Basic customer orientation / handoff</p>
        </div>
        {gapMedium}
        <div style={gs.flowRootWidth100}>
          <p style={sx.node}>
            <span style={sx.bold}>2. Installation Verification.</span> The
            contractor confirms that, at the time of completion, the following
            verifications were performed:
          </p>

          <p style={sx.nodeBullet}>• Equipment securely mounted and powered</p>
          <p style={sx.nodeBullet}>• Live camera views confirmed</p>
          <p style={sx.nodeBullet}>• Recording functionality verified</p>
          <p style={sx.nodeBullet}>• App access and remote viewing enabled</p>
          <p style={sx.nodeBullet}>• System walkthrough provided to client</p>
          <p style={sx.nodeBullet}>• Work area left safe and orderly</p>
        </div>
        {gapMedium}
        <div style={gs.flowRootWidth100}>
          <p style={sx.node}>
            <span style={sx.bold}>3. Client Acknowledgment.</span> By signing
            below, the client acknowledges that:
          </p>

          <p style={sx.nodeBullet}>
            • The system described above has been installed and demonstrated
          </p>
          <p style={sx.nodeBullet}>
            • The system was operational at the time of completion
          </p>
          <p style={sx.nodeBullet}>
            • Any future changes, upgrades, or troubleshooting beyond this scope
            may require additional service fees
          </p>
          <p style={sx.nodeBullet}>
            • Ongoing app access, cloud features, or manufacturer services are
            subject to third-party terms and availability
          </p>
        </div>
        {gapMedium}
        <div style={gs.flowRootWidth100}>
          <p style={sx.node}>
            <span style={sx.bold}>4. Warranty & Limitations.</span>
          </p>

          <p style={sx.nodeBullet}>
            • Equipment warranties are provided by the manufacturer
          </p>
          <p style={sx.nodeBullet}>
            • Contractor is not responsible for internet outages, manufacturer
            app changes, or third-party service disruptions
          </p>

          {gapBig}

          <div className={styles['signatures']}>
            <div className={styles['signature-container']}>
              <div>
                <p className={styles['label-signature']}>Property owner:</p>

                <span>{customerName}</span>
              </div>

              {gs.rowGap24}

              {security_completion_sign_customer && (
                <>
                  <img
                    alt=""
                    className={styles['signature-img']}
                    src={security_completion_sign_customer}
                    width={200}
                  />
                  <p>
                    {c.formatContractDate(
                      security_completion_sign_customer_date,
                      solarCompany,
                      'MM/DD/YYYY - hh:mma',
                    )}
                  </p>
                </>
              )}

              <p>Address: {customerAddress}</p>

              {gs.rowGap24}

              {isPDF || security_completion_sign_customer ? (
                <div />
              ) : (
                <Button
                  onClick={toggleSecCompletionSignature}
                  sx={buttonSignatureStyle}
                  variant="contained"
                >
                  Client Signature
                </Button>
              )}
            </div>

            <div className={styles['signature-container']}>
              <div>
                <p className={styles['label-signature']}></p>
                <span> </span>
              </div>

              {gs.rowGap24}

              <p className={styles['label-signature']}></p>
              <p className={styles['label-signature']}></p>

              {gs.rowGap24}

              <div />
            </div>
          </div>
        </div>
      </ReportPages>
    )
  },
)

const gapBig = gs.rowGap48
const gapMedium = gs.rowGap32
const gapSmall = gs.rowGap16

const sxNode = {
  alignSelf: 'flex-start',
  fontFamily: c.light.fontFamily,
  margin: 0,
}
const sx = {
  companyInfo: {
    fontFamily: c.light.fontFamily,
    margin: 0,
    textAlign: 'left' as const,
  },
  headerHeading: { textAlign: 'left' as const },
  headerText: { marginLeft: 220 },
  logo: { left: horizontalPad, position: 'absolute' as const, top: '96px' },
  node: sxNode,
  nodeBullet: { ...sxNode, marginLeft: '24px' },
  bold: { ...sxNode, fontWeight: 'bold' },
  grandTotal: { ...sxNode, textAlign: 'end' as const, width: '100%' },
}
